import type { DestinoTrasladoDTO, NotificacionDTO, RecepcionInput, TrasladoDTO, TrasladoInput, TrasladoResumenDTO, VehiculoDTO } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/lib/api.ts';
import { conClave, useEnvioUnico } from '@/lib/idempotencia.ts';

export interface FiltrosTraslados {
  direccion?: 'entrantes' | 'salientes' | undefined;
  estado?: string | undefined;
  q?: string | undefined;
  desde?: string | undefined;
  hasta?: string | undefined;
  almacen_origen_id?: string | undefined;
  almacen_destino_id?: string | undefined;
  con_diferencia?: 'true' | 'false' | undefined;
  pagina?: number | undefined;
  limite?: number | undefined;
}
export interface PaginaTraslados {
  datos: TrasladoResumenDTO[];
  total: number;
  pagina: number;
  limite: number;
}

export const clavesTraslados = {
  lista: (f: FiltrosTraslados) => ['traslados', 'lista', f] as const,
  detalle: (id: string) => ['traslados', 'detalle', id] as const,
  destinos: ['traslados', 'destinos'] as const,
};

export const useTraslados = (f: FiltrosTraslados) => useQuery({ queryKey: clavesTraslados.lista(f), queryFn: () => get<PaginaTraslados>('/traslados', f as Record<string, string | number | undefined>), placeholderData: (p) => p });
export const useTraslado = (id: string | null) => useQuery({ queryKey: clavesTraslados.detalle(id ?? ''), queryFn: () => get<TrasladoDTO>(`/traslados/${id}`), enabled: !!id });
export const useDestinosTraslado = (habilitado = true) => useQuery({ queryKey: clavesTraslados.destinos, queryFn: () => get<DestinoTrasladoDTO[]>('/traslados/destinos'), enabled: habilitado, staleTime: 5 * 60_000 });
/** Vehículos de la ficha de flota para elegir en la guía (solo nombre y placa). */
export const useVehiculos = (habilitado = true) => useQuery({ queryKey: ['flota', 'vehiculos'], queryFn: () => get<VehiculoDTO[]>('/flota/vehiculos'), enabled: habilitado, staleTime: 5 * 60_000 });
export const registrarImpresionTraslado = (id: string) => post<{ impresiones: number; copia: boolean }>(`/traslados/${id}/impresion`);

function useInvalidar() {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: ['traslados'] }), qc.invalidateQueries({ queryKey: ['inventario'] }), qc.invalidateQueries({ queryKey: ['productos'] }), qc.invalidateQueries({ queryKey: ['notificaciones'] })]);
}
export function useDespacharTraslado() {
  const inv = useInvalidar();
  // Reintentar tras un corte de red no despacha otra guía: va la misma clave y el servidor devuelve la que ya hizo
  const enviar = useEnvioUnico((datos: TrasladoInput, clave: string) => post<TrasladoDTO>('/traslados', datos, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => void inv() });
}
export function useRecibirTraslado() {
  const inv = useInvalidar();
  const enviar = useEnvioUnico(({ id, datos }: { id: string; datos: RecepcionInput }, clave: string) => post<TrasladoDTO>(`/traslados/${id}/recibir`, datos, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => void inv() });
}
/** Visto bueno de la dueña a una diferencia que quedó en el origen. */
export function useRevisarDiferencia() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, comentario }: { id: string; comentario?: string | undefined }) => post<TrasladoDTO>(`/traslados/${id}/diferencia/revisar`, comentario ? { comentario } : {}), onSuccess: () => void inv() });
}
export function useCancelarTraslado() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, motivo }: { id: string; motivo: string }) => post<TrasladoDTO>(`/traslados/${id}/cancelar`, { motivo }), onSuccess: () => void inv() });
}

// ─── Notificaciones (header) ────────────────────────────────────────────────
export const clavesNotificaciones = ['notificaciones'] as const;
export const useNotificaciones = (habilitado: boolean) => useQuery({ queryKey: clavesNotificaciones, queryFn: () => get<{ datos: NotificacionDTO[]; no_leidas: number }>('/notificaciones'), enabled: habilitado, refetchInterval: 15_000, staleTime: 10_000 });
export function useLeerNotificacion() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string | null) => post<{ ok: boolean }>(id ? `/notificaciones/${id}/leer` : '/notificaciones/leer-todas'), onSuccess: () => void qc.invalidateQueries({ queryKey: clavesNotificaciones }) });
}
